/**
 * @fileoverview Sector, activity, payment, and company-size fields for Corporate Identity.
 */

import Select from "react-select";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/CorporateIdentity/CalendarIcon.svg";
import { ReactComponent as UserIcon } from "../../../assets/icons/CorporateIdentity/UserIcon.svg";
import {
  formatDate,
  getSafeSelectedOption,
  getSelectedOption,
  isFieldReadOnlyForProfile,
  selectStyles,
  shouldHideFieldForProfile,
} from "../../../utils/common";
import styles from "./CorporateIdentity.module.css";
import {
  CURSOR_NOT_ALLOWED,
  disabledSelectStyles,
  NOT_VISIBLE_FIELD_MESSAGE_KEY,
  referencedDataSelectProps,
} from "./corporateIdentityConstants";

function CorporateIdentityClassificationRow({
  company,
  selectedProfile,
  section,
}) {
  const {
    t,
    isMobile,
    sectors,
    sectorEconomico,
    setSectorEconomico,
    setSubsectors,
    branches,
    ramaSCIAN,
    setRamaSCIAN,
    handleInputChange,
  } = section;

  return (
    (!shouldHideFieldForProfile("fechaUltimoPago", selectedProfile) ||
      !shouldHideFieldForProfile("ejecutivoDeAtencion", selectedProfile) ||
      !shouldHideFieldForProfile("sectorEconomico", selectedProfile) ||
      !shouldHideFieldForProfile("ramaSCIAN", selectedProfile)) && (
      <div className={styles.inputRow}>
        {!shouldHideFieldForProfile("fechaUltimoPago", selectedProfile) && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.lastPaymentDateLabel")}
            </label>
            <div className={styles.inputWrapper}>
              <CalendarIcon className={styles.inputIcon} />
              <input
                type="text"
                className={`${styles.input} ${styles.inputWithIcon}`}
                placeholder={t(
                  "components.corporateIdentity.lastPaymentDatePlaceholder",
                )}
                value={formatDate(company?.fechaUltimoPago)}
                disabled={true}
                style={{ cursor: CURSOR_NOT_ALLOWED }}
                onChange={(e) =>
                  handleInputChange("fechaUltimoPago", e.target.value)
                }
              />
            </div>
          </div>
        )}
        {!shouldHideFieldForProfile(
          "ejecutivoDeAtencion",
          selectedProfile,
        ) && (
          <div
            className={`${styles.inputGroup} ${
              isMobile ? styles.mobileTopSpacing : ""
            }`}
          >
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.serviceExecutiveLabel")}
            </label>
            <div className={styles.inputWrapper}>
              <UserIcon className={styles.inputIcon} />
              <input
                type="text"
                className={`${styles.input} ${styles.inputWithIcon}`}
                placeholder={t(
                  "components.corporateIdentity.serviceExecutivePlaceholder",
                )}
                value={company?.ejecutivoDeAtencion || ""}
                disabled={true}
                style={{ cursor: CURSOR_NOT_ALLOWED }}
                onChange={(e) =>
                  handleInputChange("ejecutivoDeAtencion", e.target.value)
                }
              />
            </div>
            <p className={styles.notVisibleFieldMessage}>
              *{t(NOT_VISIBLE_FIELD_MESSAGE_KEY)}
            </p>
          </div>
        )}
        {!shouldHideFieldForProfile("sectorEconomico", selectedProfile) && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.economicSectorLabel")}
            </label>
            <Select
              options={sectors}
              value={getSafeSelectedOption(sectors, sectorEconomico)}
              placeholder={t(
                "components.corporateIdentity.economicSectorPlaceholder",
              )}
              styles={disabledSelectStyles}
              className="select__control"
              isDisabled={true}
              {...referencedDataSelectProps}
              onChange={(selectedOption) => {
                setSectorEconomico(selectedOption._id);
                handleInputChange("sectorEconomico", selectedOption._id);
                setSubsectors(selectedOption.subSectores ?? []);
              }}
            />
          </div>
        )}
        {!shouldHideFieldForProfile("ramaSCIAN", selectedProfile) && (
          <div
            className={`${styles.inputGroup} ${
              isMobile ? styles.mobileTopSpacing : ""
            }`}
          >
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.economicActivityLabel")}
            </label>
            <Select
              key={`rama-${branches.length}-${ramaSCIAN}`}
              value={getSafeSelectedOption(branches, ramaSCIAN)}
              placeholder={t(
                "components.corporateIdentity.economicActivityPlaceholder",
              )}
              styles={disabledSelectStyles}
              className="select__control"
              isDisabled={true}
              options={branches}
              {...referencedDataSelectProps}
              onChange={(selectedOption) => {
                setRamaSCIAN(selectedOption._id);
                handleInputChange("ramaSCIAN", selectedOption._id);
              }}
            />
            <p className={styles.notVisibleFieldMessage}>
              *{t(NOT_VISIBLE_FIELD_MESSAGE_KEY)}
            </p>
          </div>
        )}
      </div>
    )
  );
}

function CorporateIdentityOrganizationRow({
  selectedProfile,
  section,
}) {
  const {
    t,
    subSectors,
    subSectorSCIAN,
    setSubSectorSCIAN,
    setBranches,
    setRamaSCIAN,
    businessGroups,
    grupoEmpresarial,
    setGrupoEmpresarial,
    operationEnvironmentOptions,
    ambitoOperaciones,
    setAmbitoOperaciones,
    companySizeOptions,
    tamanoDeLaEmpresa,
    setTamanoDeLaEmpresa,
    handleInputChange,
  } = section;

  return (
    (!shouldHideFieldForProfile("subSectorSCIAN", selectedProfile) ||
      !shouldHideFieldForProfile("grupoEmpresarial", selectedProfile) ||
      !shouldHideFieldForProfile("ambitoOperaciones", selectedProfile) ||
      !shouldHideFieldForProfile("tamanoDeLaEmpresa", selectedProfile)) && (
      <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
        {!shouldHideFieldForProfile("subSectorSCIAN", selectedProfile) && (
          <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.subsectorLabel")}
            </label>
            <Select
              key={`subsector-${subSectors.length}-${subSectorSCIAN}`}
              value={getSafeSelectedOption(subSectors, subSectorSCIAN)}
              placeholder={t(
                "components.corporateIdentity.subsectorPlaceholder",
              )}
              styles={disabledSelectStyles}
              className="select__control"
              isDisabled={true}
              options={subSectors}
              {...referencedDataSelectProps}
              onChange={(selectedOption) => {
                setSubSectorSCIAN(selectedOption._id);
                handleInputChange("subSectorSCIAN", selectedOption._id);
                setBranches(selectedOption.ramas ?? []);
                setRamaSCIAN(null);
              }}
            />
            <p className={styles.notVisibleFieldMessage}>
              *{t(NOT_VISIBLE_FIELD_MESSAGE_KEY)}
            </p>
          </div>
        )}
        {!shouldHideFieldForProfile("grupoEmpresarial", selectedProfile) && (
          <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.businessGroupLabel")}
            </label>
            <Select
              value={getSafeSelectedOption(businessGroups, grupoEmpresarial)}
              placeholder={t(
                "components.corporateIdentity.businessGroupPlaceholder",
              )}
              styles={disabledSelectStyles}
              className="select__control"
              isDisabled={true}
              options={businessGroups}
              {...referencedDataSelectProps}
              onChange={(selectedOption) => {
                setGrupoEmpresarial(selectedOption._id);
                handleInputChange("grupoEmpresarial", selectedOption._id);
              }}
            />
            <p className={styles.notVisibleFieldMessage}>
              *{t(NOT_VISIBLE_FIELD_MESSAGE_KEY)}
            </p>
          </div>
        )}
        {!shouldHideFieldForProfile("ambitoOperaciones", selectedProfile) && (
          <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.operationEnvironmentLabel")}
            </label>
            <Select
              value={getSelectedOption(
                operationEnvironmentOptions,
                ambitoOperaciones,
              )}
              placeholder={t(
                "components.corporateIdentity.operationEnvironmentPlaceholder",
              )}
              options={operationEnvironmentOptions}
              styles={selectStyles}
              className="select__control"
              onChange={(selectedOption) => {
                setAmbitoOperaciones(selectedOption.value);
                handleInputChange("ambitoOperaciones", selectedOption.value);
              }}
              isDisabled={true}
            />
          </div>
        )}
        {!shouldHideFieldForProfile("tamanoDeLaEmpresa", selectedProfile) && (
          <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
            <label className={styles.inputLabel}>
              {t("components.corporateIdentity.companySizeLabel")}
            </label>
            <Select
              value={getSelectedOption(companySizeOptions, tamanoDeLaEmpresa)}
              options={companySizeOptions}
              placeholder={t(
                "components.corporateIdentity.companySizePlaceholder",
              )}
              styles={{
                ...selectStyles,
                control: (provided, state) => ({
                  ...(selectStyles.control
                    ? selectStyles.control(provided, state)
                    : provided),
                  cursor: isFieldReadOnlyForProfile(
                    "tamanoDeLaEmpresa",
                    selectedProfile,
                  )
                    ? CURSOR_NOT_ALLOWED
                    : "pointer",
                }),
              }}
              className="select__control"
              isDisabled={true}
              onChange={(selectedOption) => {
                setTamanoDeLaEmpresa(selectedOption.value);
                handleInputChange("tamanoDeLaEmpresa", selectedOption.value);
              }}
            />
          </div>
        )}
      </div>
    )
  );
}

export default function CorporateIdentitySectorFields({
  company,
  selectedProfile,
  section,
}) {
  return (
    <>
      <CorporateIdentityClassificationRow
        company={company}
        selectedProfile={selectedProfile}
        section={section}
      />
      <CorporateIdentityOrganizationRow
        selectedProfile={selectedProfile}
        section={section}
      />
    </>
  );
}
